import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';

import { openIntercomChat } from '@client/util/intercomUtil';
import { SessionType } from '@shared';
import {
  ArrowRightLeft,
  BarChart3,
  Blocks,
  Book,
  ClipboardCheck,
  CreditCard,
  House,
  Info,
  KeyRound,
  Link2,
  LogOut,
  Menu,
  MessageCircleQuestion,
  Plug,
  Rocket,
  Send,
  UserCog,
  UserPen,
  Users,
  FileUser,
  Sparkles,
} from 'lucide-react';
import React, { FunctionComponent, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { QuickPlanDialog, useQuickPlanAvailability } from '@client/features/client-quick-plan';
import { fetchClientsForAdvisorByAdvisorIdRequest } from '..';
import { About, DashboardActivePage, HrefLink, PageLink, WaterlilyLogo, cn } from '..';
import {
  selectSession,
  selectSessionAdvisor,
  selectSponsorshipAccessStatus,
} from '../model/selectors';
import { SideBarNotificationBubble } from './NotificationBubble';
import { Button } from './ui/button';
import { useInviteClientTrigger } from '@client/features/invite-client';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useRoleSwitch } from '@/hooks/useRoleSwitch';
import { PilotRestricted } from '@/shared/components/pilot-restricted';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import {
  useIsPilotRestricted,
  usePilotSelfPlanIntakeForm,
} from '@/shared/hooks/use-active-pilot-program';
import { DEFAULT_DASHBOARD_CONFIG, type DashboardConfig } from './dashboard-config';

type AdvisorDashboardSideBarProps = {
  children: React.ReactNode;
  advisorSideBarMobile: React.ReactNode;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
};

const getContactSupportUrl = (advisor: Advisor) =>
  `https://waterlily.typeform.com/to/yXX4wrjU#email=${advisor.advisorEmail}&first_name=${advisor.advisorFirstName}&advisor_id=${advisor.advisorId}`;

export function AdvisorDashboardSideBar({
  children,
  advisorSideBarMobile,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
}: AdvisorDashboardSideBarProps) {
  return (
    <>
      {/* Mobile sidebar */}
      {advisorSideBarMobile}

      {/* Static sidebar, desktop */}
      <div
        className={cn(
          'group hidden transition-all duration-300 lg:sticky lg:top-0 lg:z-50 lg:flex lg:h-screen lg:flex-col',
          isSidebarCollapsed ? 'overflow-hidden lg:w-0' : 'lg:w-72',
        )}
      >
        <div className="relative flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
          {/* Collapse button when expanded */}
          {!isSidebarCollapsed && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="
                  bg-primary border-primary-300 hover:bg-primary-hover absolute right-2 top-2 hidden
                  h-7 w-7 rounded-md border
                  text-white
                  opacity-0 transition-all
                  duration-200 hover:text-white
                  group-hover:opacity-100 lg:flex
                "
                id="collapse-sidebar"
                data-testid="collapse-sidebar"
                onClick={() => setIsSidebarCollapsed(true)}
              >
                <span className="sr-only">Collapse sidebar</span>
                <Menu className="h-4 w-4" />
              </Button>
              {children}
            </>
          )}
        </div>
      </div>
    </>
  );
}

type AdvisorDashboardContentProps = {
  onPageChange: (page: DashboardActivePage) => void;
  onNavigateToOnboarding: () => void;
  hasIncompleteOnboarding?: boolean;
  isDisabled: boolean;
  activePage: DashboardActivePage;
  handleClickSignOut: () => void;
  sidebarConfig?: DashboardConfig['sidebar'];
};

export function AdvisorDashboardSideBarContent({
  onPageChange,
  onNavigateToOnboarding,
  hasIncompleteOnboarding,
  isDisabled,
  activePage,
  handleClickSignOut,
  sidebarConfig = DEFAULT_DASHBOARD_CONFIG.sidebar,
}: AdvisorDashboardContentProps) {
  const { sessionType, hasClientAccount } = useSelector(selectSession);
  const { isPartOfSponsorship } = useSelector(selectSponsorshipAccessStatus);
  const advisor = useSelector(selectSessionAdvisor);
  const { setInviteClientParams } = useInviteClientTrigger();
  const { flags } = useFeatureFlags();
  const { toggleRole } = useRoleSwitch();
  const dispatch = useDispatch();
  const useFormProvider = flags.useFormProvider ?? false;
  const hideEmailAndFillIntakeForm = flags?.hideInviteEmailAndFillIntakeForm;
  const isEdjPilot = useIsEdjPilotAdvisor();
  const isEngagementCampaignsRestricted = useIsPilotRestricted('engagement-campaigns');
  const hasSelfPlanIntakeForm = !!usePilotSelfPlanIntakeForm();

  const [quickPlanOpen, setQuickPlanOpen] = useState(false);
  // EDJ restricts `quick-start`; `edjQuickPlan` re-opens it with the EDJ form for flagged advisors.
  const { isEdjQuickPlan } = useQuickPlanAvailability();
  const quickStartButton = (
    <SideBarButton
      label="New Case Quick Start"
      onClick={() => setQuickPlanOpen(true)}
      icon={<FileUser strokeWidth={1.5} />}
      isDisabled={isDisabled}
    />
  );
  const handleQuickPlanSuccess = useCallback(() => {
    setQuickPlanOpen(false);
    dispatch(
      fetchClientsForAdvisorByAdvisorIdRequest({
        advisorId: advisor.advisorId,
        isInvalidation: false,
      }),
    );
  }, [dispatch, advisor.advisorId]);

  const engagementCampaignsUrl = isEdjPilot
    ? 'https://help.waterlily.com/en/collections/19724501-engagement-campaigns-for-the-broker-dealer'
    : 'https://help.waterlily.com/en/collections/9811062-engagement-campaigns';

  const homeButton = (
    <SideBarButton
      id="sidebar-home"
      data-testid="sidebar-home"
      label="Home"
      onClick={() => onPageChange('clients')}
      icon={<House strokeWidth={1.5} />}
      isActive={activePage === 'clients'}
    />
  );

  const advisorProfileButton = (
    <SideBarButton
      id="sidebar-advisor-profile"
      data-testid="sidebar-advisor-profile"
      label="Advisor Profile"
      onClick={() => onPageChange('settings')}
      icon={<UserCog strokeWidth={1.5} />}
      isDisabled={isDisabled}
      isActive={activePage === 'settings'}
    />
  );

  const sponsorshipButton = (
    <SideBarButton
      id="sidebar-sponsorship"
      label="Sponsorship"
      onClick={() => onPageChange('sponsorship')}
      icon={<Users strokeWidth={1.5} />}
      isDisabled={isDisabled}
      isActive={activePage === 'sponsorship'}
    />
  );

  const seatsButton = (
    <SideBarButton
      id="sidebar-seats"
      label="Subscription"
      onClick={() => onPageChange('seats')}
      icon={<CreditCard strokeWidth={1.5} />}
      isActive={activePage === 'seats'}
    />
  );

  const teamButton = (
    <SideBarButton
      id="sidebar-team"
      label="Team"
      onClick={() => onPageChange('team')}
      icon={<Users strokeWidth={1.5} />}
      isDisabled={isDisabled}
      isActive={activePage === 'team'}
    />
  );

  const integrationsButton = (
    <PilotRestricted
      capability="crm-integration"
      side="right"
    >
      <SideBarButton
        id="sidebar-integrations"
        data-testid="sidebar-integrations"
        label="Integrations"
        onClick={() => onPageChange('integrations')}
        icon={<Plug strokeWidth={1.5} />}
        isDisabled={isDisabled}
        isActive={activePage === 'integrations'}
      />
    </PilotRestricted>
  );

  const usageButton = (
    <SideBarButton
      id="sidebar-usage"
      label="Metrics"
      onClick={() => onPageChange('usage')}
      icon={<BarChart3 strokeWidth={1.5} />}
      isDisabled={isDisabled}
      isActive={activePage === 'usage'}
    />
  );

  const adminPortalButton = (
    <SideBarButton
      id="sidebar-admin-portal"
      label="Admin Portal"
      onClick={() => onPageChange('admin-portal')}
      icon={<KeyRound strokeWidth={1.5} />}
      isActive={activePage === 'admin-portal'}
    />
  );

  const metricsButton = (
    <SideBarButton
      id="sidebar-metrics"
      label="Wizard Metrics"
      onClick={() => onPageChange('metrics')}
      icon={<Sparkles strokeWidth={1.5} />}
      isActive={activePage === 'metrics'}
    />
  );

  const formBuilderLink = (
    <HrefLink
      to="/wizard/form-builder"
      className="text-primary-dark hover:bg-primary-200 hover:text-primary group flex gap-x-3 rounded-xl px-3 py-2 text-sm leading-6"
    >
      <Blocks
        className="text-primary-dark group-hover:text-primary h-6 w-6 shrink-0"
        strokeWidth={1.5}
        aria-hidden="true"
      />
      Form Builder
    </HrefLink>
  );

  return (
    <>
      <QuickPlanDialog
        open={quickPlanOpen}
        onOpenChange={setQuickPlanOpen}
        advisorId={advisor.advisorId}
        onSubmitSuccess={handleQuickPlanSuccess}
        initialMode={sidebarConfig.quickStartDirect ? 'quick-plan' : undefined}
        lockMode={sidebarConfig.quickStartDirect}
      />
      {renderLogoLink(() => onPageChange('clients'), 'mt-4')}
      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-5">
          <li>
            <ul className="space-y-1">
              <SideBarHeader
                title="Dashboard"
                isDisabled={isDisabled}
              />
              <li key="clients">{homeButton}</li>
              {/* <li key="opportunities">{opportunitiesButton}</li> */}
              <li key="advisorProfile">{advisorProfileButton}</li>
              {sidebarConfig.showIntegrations && flags?.showIntegrations && (
                <li key="integrations">{integrationsButton}</li>
              )}
              {isPartOfSponsorship && <li key="sponsorship">{sponsorshipButton}</li>}
            </ul>
          </li>
          <li>
            <ul className="space-y-1">
              <SideBarHeader
                title="Actions"
                isDisabled={isDisabled}
              />
              {sidebarConfig.showFillIntakeForm &&
                useFormProvider &&
                (!hideEmailAndFillIntakeForm || isEdjPilot) && (
                  <li key="fill-intake-form">
                    {hasSelfPlanIntakeForm ? (
                      <SideBarButton
                        label="Plan for Yourself"
                        onClick={() => setInviteClientParams('self-test')}
                        icon={<UserPen strokeWidth={1.5} />}
                        isDisabled={isDisabled}
                      />
                    ) : (
                      <PilotRestricted
                        capability="intake-form"
                        side="right"
                      >
                        <SideBarButton
                          label="Fill Intake Form"
                          onClick={() => setInviteClientParams('fill-form')}
                          icon={<UserPen strokeWidth={1.5} />}
                          isDisabled={isDisabled}
                        />
                      </PilotRestricted>
                    )}
                  </li>
                )}
              {sidebarConfig.showInviteClient && (!hideEmailAndFillIntakeForm || isEdjPilot) && (
                <li key="invite-client">
                  <PilotRestricted
                    capability="invite-client"
                    side="right"
                  >
                    <SideBarButton
                      label="Invite Client"
                      onClick={() => setInviteClientParams('email')}
                      icon={<Send strokeWidth={1.5} />}
                      isDisabled={isDisabled}
                    />
                  </PilotRestricted>
                </li>
              )}
              {sidebarConfig.showIntakeFormLink && (
                <li key="intake-form-link">
                  <SideBarButton
                    label="Intake Form Link"
                    onClick={() => setInviteClientParams('link')}
                    icon={<Link2 strokeWidth={1.5} />}
                    isDisabled={isDisabled}
                  />
                </li>
              )}
              {!hasIncompleteOnboarding && (
                <li key="onboarding">
                  <SideBarButton
                    label="Onboarding"
                    onClick={onNavigateToOnboarding}
                    icon={<ClipboardCheck strokeWidth={1.5} />}
                    isDisabled={isDisabled}
                  />
                </li>
              )}
              <li key="new-case-quick-start">
                {isEdjQuickPlan ? (
                  quickStartButton
                ) : (
                  <PilotRestricted
                    capability="quick-start"
                    side="right"
                  >
                    {quickStartButton}
                  </PilotRestricted>
                )}
              </li>
            </ul>
          </li>
          {sidebarConfig.showEngagementCampaigns && !isEngagementCampaignsRestricted && (
            <li>
              <ul className=" mt-2 space-y-1">
                <SideBarHeader title="Marketing" />
                <li key="marketing">
                  {renderExternalLink(
                    engagementCampaignsUrl,
                    'Engagement Campaigns',
                    <Rocket strokeWidth={1.5} />,
                  )}
                </li>
              </ul>
            </li>
          )}
          <li>
            <ul className=" mt-2 space-y-1">
              <SideBarHeader title="Support" />
              <li key="contact-support">
                <SideBarButton
                  label="Contact Support"
                  onClick={openIntercomChat}
                  icon={<MessageCircleQuestion strokeWidth={1.5} />}
                  isDisabled={isDisabled}
                />
              </li>
              <li key="help-desk">
                {renderExternalLink(
                  'https://help.joinwaterlily.com',
                  'Help Desk',
                  <Book strokeWidth={1.5} />,
                )}
              </li>
            </ul>
          </li>
          <li>
            <ul className="mt-2 space-y-1">
              <SideBarHeader title="Other" />
              {sidebarConfig.showSubscription && <li key="seats">{seatsButton}</li>}
              {sidebarConfig.showTeam && <li key="team">{teamButton}</li>}
              {sidebarConfig.showMetrics && flags?.usageMetrics && (
                <li key="usage">{usageButton}</li>
              )}
              {sessionType === SessionType.wizard && (
                <>
                  <li key="advisorMetrics">{metricsButton}</li>
                  <li key="advisorAdminPortal">{adminPortalButton}</li>
                  <li key="formBuilder">{formBuilderLink}</li>
                </>
              )}
              {flags?.advisorClientLogin && hasClientAccount && (
                <li key="switch-to-client">
                  <SideBarButton
                    label="Switch to Client View"
                    onClick={toggleRole}
                    icon={<ArrowRightLeft strokeWidth={1.5} />}
                    isDisabled={false}
                  />
                </li>
              )}
              <li key="signout">
                <SideBarButton
                  label="Sign Out"
                  onClick={handleClickSignOut}
                  icon={<LogOut strokeWidth={1.5} />}
                  isDisabled={false}
                />
              </li>
            </ul>
          </li>
          <li className="mt-auto border-t border-gray-100 pt-2">
            <ul className="mt-2 space-y-1">
              <li key="about">
                {renderNavLink(
                  About,
                  'About',
                  <Info strokeWidth={1.5} />,
                  'bg-primary-200 text-primary-dark',
                  false,
                )}
              </li>
            </ul>
          </li>
        </ul>
      </nav>
    </>
  );
}

function SideBarHeader({
  title,
  className,
  isDisabled = false,
}: {
  title: string;
  className?: string;
  isDisabled?: boolean;
}) {
  return (
    <div
      className={cn('text-sm font-normal leading-6 text-gray-700', className, {
        'opacity-50': isDisabled,
      })}
    >
      {title}
    </div>
  );
}

type AdvisorDashboardSideBarMobileProps = {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  children: React.ReactNode;
};

export function AdvisorDashboardSideBarMobile({
  sidebarOpen,
  setSidebarOpen,
  children,
}: AdvisorDashboardSideBarMobileProps) {
  return (
    <Dialog
      className="relative z-50 lg:hidden"
      open={sidebarOpen}
      onClose={setSidebarOpen}
    >
      <DialogBackdrop
        transition
        className="data-closed:opacity-0 fixed inset-0 bg-white transition-opacity duration-100 ease-linear"
      />

      <div className="fixed inset-0 flex">
        <DialogPanel
          transition
          className="data-closed:-translate-x-full relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-100 ease-in-out"
        >
          <TransitionChild>
            <div className="data-closed:opacity-0 absolute left-full top-0 flex w-16 justify-center pt-5 duration-100 ease-in-out">
              <button
                type="button"
                className="-m-2.5 p-2.5"
                onClick={() => setSidebarOpen(false)}
              >
                <span className="sr-only">Close sidebar</span>
                <Menu className="h-7 w-7 rounded-full bg-gray-100 p-1 text-gray-800" />
              </button>
            </div>
          </TransitionChild>
          {/* Sidebar component, mobile */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
            {children}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

function renderNavLink(
  to: FunctionComponent<any>,
  label: string,
  icon: JSX.Element,
  bgClass: string,
  isActive: boolean,
  targetProps?: ObjectMap<any>,
  onClick?: () => void,
) {
  return (
    <PageLink
      to={to}
      targetProps={targetProps}
      onClick={onClick}
      className={cn(
        isActive ? bgClass : 'text-primary-dark hover:bg-primary-200 hover:text-primary',
        'group flex gap-x-3 rounded-xl px-3 py-2 text-sm leading-6',
      )}
    >
      {React.cloneElement(icon, {
        'className': cn(
          isActive ? 'text-primary' : 'text-primary-dark group-hover:text-primary',
          'h-6 w-6 shrink-0',
        ),
        'aria-hidden': 'true',
      })}
      {label}
    </PageLink>
  );
}

interface SideBarButtonProps {
  'id'?: string;
  'data-testid'?: string;
  'label': string;
  'onClick': () => void;
  'icon': JSX.Element;
  'isDisabled'?: boolean;
  'isActive'?: boolean;
  'showNotificationBubble'?: boolean;
}

function SideBarButton({
  id,
  'data-testid': dataTestId,
  label,
  onClick,
  icon,
  isDisabled = false,
  isActive = false,
  showNotificationBubble = false,
}: SideBarButtonProps) {
  return (
    <button
      id={id}
      data-testid={dataTestId}
      className={cn(
        'group flex w-full gap-x-3 rounded-xl px-3 py-2 text-sm leading-6',
        isDisabled
          ? 'text-primary-dark cursor-default opacity-50'
          : isActive
            ? 'bg-primary-200 text-primary-dark'
            : 'text-primary-dark hover:bg-primary-200 hover:text-primary',
      )}
      onClick={() => {
        if (!isDisabled) {
          onClick();
        }
      }}
      disabled={isDisabled}
    >
      {React.cloneElement(icon, {
        'className': cn(
          'h-6 w-6 shrink-0',
          isDisabled
            ? 'text-primary-dark'
            : isActive
              ? 'text-primary'
              : 'text-primary-dark group-hover:text-primary',
        ),
        'aria-hidden': 'true',
      })}
      {label}
      {!isDisabled && showNotificationBubble && <SideBarNotificationBubble className="my-auto" />}
    </button>
  );
}

function renderExternalLink(url: string, label: string, icon: JSX.Element) {
  return (
    <a
      className="text-primary-dark hover:bg-primary-200 hover:text-primary group flex gap-x-3 rounded-xl px-3 py-2 text-sm  leading-6"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {React.cloneElement(icon, {
        'className': 'h-6 w-6 shrink-0 text-primary-dark group-hover:text-primary',
        'aria-hidden': 'true',
      })}
      {label}
    </a>
  );
}

function renderLogoLink(onClick: () => void, className?: string) {
  return (
    <button
      onClick={onClick}
      data-testid="waterlily-logo-button"
      className={cn('flex h-16 shrink-0 items-center gap-3', className)}
    >
      <WaterlilyLogo className="h-10" />
    </button>
  );
}
